import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CssContentPreviewRequest, CssValueDetail } from '../../../type/cssTemplate';
import { useProductStore } from '../../../store/useProduct';
import { useLoginStore } from '../../../store/useLogin';
import { useCssValueTemplateStore } from '../../../store/useCssValueTemplateStore';
import apiClient from '../../../service/apiClient';

interface StyleListPanelProps {
  categoryName: string | null;
  contents: CssValueDetail[];
  productId: string;
  selectedIds: string[]; // 전체 카테고리에서 선택된 cssContentId 목록
  currentSelectedId?: string | null; // 현재 카테고리에서 선택된 항목
  onSelect?: (id: string) => void; // 현재 카테고리 선택 변경 콜백
}

const StyleListPanel: React.FC<StyleListPanelProps> = ({ categoryName, contents, productId, selectedIds, currentSelectedId, onSelect }) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const scrollPositionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const lastPreviewRequestKeyRef = useRef<string>('');
  const previewRequestInFlightRef = useRef<string | null>(null);
  const previewCacheRef = useRef<Map<string, string>>(new Map());
  const previewRequestTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const { getProductPreview, getProductDetail, productDetail } = useProductStore();
  const { role } = useLoginStore();
  const isAdmin = role === 'ROLE_ADMIN';
  const { values: storeValues, addPendingItem, setPendingOrigin, addPendingCategory, pendingCreates } = useCssValueTemplateStore() as any;
  const [showCreateItem, setShowCreateItem] = useState(false);
  const [newItem, setNewItem] = useState({ cssName: '', name: '', uploadFileId: '', cssValue: '' });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState<string>('');
  const [showEditItem, setShowEditItem] = useState(false);
  const [editingTargetId, setEditingTargetId] = useState<string | null>(null);
  const [editItem, setEditItem] = useState({ cssContentId: '', cssName: '', name: '', cssValue: '', uploadFileId: '', previewUrl: '', isUsed: false });
  const [isEditUploading, setIsEditUploading] = useState(false);
  const [editUploadFiles, setEditUploadFiles] = useState<File[]>([]);
  const [originCssName, setOriginCssName] = useState('');
  const [newItemError, setNewItemError] = useState('');
  const [editItemError, setEditItemError] = useState('');
  const lastOriginSyncRef = useRef<{ category: string | null; pending: string; response: string }>({ category: null, pending: '', response: '' });
  const pendingScrollTargetRef = useRef<{ category: string; names: string[] } | null>(null);
  const scrollPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const currentCategoryRef = useRef<string | null>(categoryName);
  const lastCategoryRef = useRef<string | null>(categoryName);
  const pendingEntryForCategory = useMemo(() => {
    if (!categoryName) return null;
    return (pendingCreates || []).find((c: any) => c.categoryName === categoryName) || null;
  }, [pendingCreates, categoryName]);
  const responseOriginForCategory = useMemo(() => {
    if (!contents || contents.length === 0) return '';
    const target = contents.find((c) => typeof c.originCssName === 'string' && c.originCssName.trim());
    return target?.originCssName?.trim() || '';
  }, [contents]);

  const pendingOriginForCategory = useMemo(() => {
    if (!categoryName || !pendingCreates) return '';
    const pendingCategory = (pendingCreates || []).find((p: any) => p.categoryName === categoryName);
    return pendingCategory?.originCssName || '';
  }, [categoryName, pendingCreates]);

  useEffect(() => {
    setOriginCssName(pendingOriginForCategory || responseOriginForCategory);
  }, [pendingOriginForCategory, responseOriginForCategory]);

  const resolveContentById = useCallback((id: string | null | undefined) => {
    if (!id) return undefined;
    return contents.find((item) => (item.cssContentId && item.cssContentId === id) || (!item.cssContentId && item.cssName === id));
  }, [contents]);
  const resetEditItem = useCallback(() => {
    setEditItem({ cssContentId: '', cssName: '', name: '', cssValue: '', uploadFileId: '', previewUrl: '', isUsed: false });
    setEditingTargetId(null);
    setEditUploadFiles([]);
    setIsEditUploading(false);
    setEditItemError('');
  }, [setEditUploadFiles, setIsEditUploading]);
  const initializeEditItem = useCallback((target: CssValueDetail) => {
    setEditItem({
      cssContentId: target.cssContentId || '',
      cssName: target.cssName || '',
      name: (target as any)?.name || '',
      cssValue: target.cssValue || '',
      uploadFileId: (target.uploadFileId as any)?.id || '',
      previewUrl: (target.uploadFileId as any)?.fileFullUrl || '',
      isUsed: target.isUsed
    });
    setEditingTargetId(target.cssContentId || target.cssName);
    setEditItemError('');
  }, []);
  const getContentIdentifier = useCallback((item: CssValueDetail | any) => {
    if (!item) return '';
    const id = typeof item.cssContentId === 'string' ? item.cssContentId.trim() : '';
    if (id) return id;
    return typeof item.cssName === 'string' ? item.cssName.trim() : '';
  }, []);
  const isDuplicateCssName = useCallback((name: string, excludeKey?: string) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return false;
    const lower = trimmed.toLowerCase();
    const matches = (contents || []).filter((item) => {
      const itemName = (item?.cssName || '').trim();
      return !!itemName && itemName.toLowerCase() === lower;
    });
    if (matches.length === 0) return false;
    if (!excludeKey) {
      return matches.length > 0;
    }
    const normalizedExclude = excludeKey.trim();
    if (matches.length === 1) {
      const identifier = getContentIdentifier(matches[0]);
      return identifier !== normalizedExclude;
    }
    return matches.some(match => getContentIdentifier(match) !== normalizedExclude);
  }, [contents, getContentIdentifier]);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);

  useEffect(() => {
    if (productId) getProductDetail(productId);
  }, [productId, getProductDetail]);

  const previewSelections = useMemo(() => {
    const resolved: CssContentPreviewRequest[] = [];
    const uniqueSelectedIds = Array.from(new Set((selectedIds || []).filter(Boolean)));

    const extractUploadId = (upload?: CssValueDetail['uploadFileId']): string | undefined => {
      if (!upload) return undefined;
      if (typeof (upload as any) === 'string') return upload as unknown as string;
      const uploadObj = upload as Record<string, any>;
      return typeof uploadObj.id === 'string' ? uploadObj.id : undefined;
    };

    const fromPending = (targetId: string): CssContentPreviewRequest | null => {
      if (!pendingCreates) return null;
      for (const group of pendingCreates) {
        const match = group.contents?.find((content: any) =>
          (content.cssContentId && content.cssContentId === targetId) ||
          (!content.cssContentId && content.cssName === targetId)
        );
        if (match) {
          return {
            cssContentId: match.cssContentId || undefined,
            categoryName: group.categoryName,
            originCssName: match.originCssName ?? group.originCssName,
            name: match.name,
            cssName: match.cssName,
            cssValue: match.cssValue,
            uploadFileId: match.uploadFileId
          };
        }
      }
      return null;
    };

    const fromValues = (targetId: string): CssContentPreviewRequest | null => {
      if (!storeValues) return null;
      for (const group of storeValues) {
        const match = group.contents?.find((content: CssValueDetail) =>
          content.cssContentId === targetId || (!content.cssContentId && content.cssName === targetId)
        );
        if (match) {
          return {
            cssContentId: match.cssContentId || undefined,
            categoryName: group.categoryName,
            originCssName: match.originCssName,
            name: match.name,
            cssName: match.cssName,
            cssValue: match.cssValue,
            uploadFileId: extractUploadId(match.uploadFileId)
          };
        }
      }
      return null;
    };

    uniqueSelectedIds.forEach(id => {
      const item = fromPending(id) || fromValues(id);
      if (item) resolved.push(item);
    });

    if (resolved.length === 0 && categoryName) {
      const fallback = (currentSelectedId && contents.find(c => (c.cssContentId || c.cssName) === currentSelectedId))
        || contents.find(c => c.isUsed)
        || contents[0];
      if (fallback) {
        const derived = fromPending(fallback.cssContentId || fallback.cssName) || fromValues(fallback.cssContentId || fallback.cssName);
        resolved.push(derived || {
          cssContentId: fallback.cssContentId || undefined,
          categoryName,
          originCssName: fallback.originCssName,
          name: (fallback as any)?.name,
          cssName: fallback.cssName,
          cssValue: fallback.cssValue,
          uploadFileId: extractUploadId(fallback.uploadFileId)
        });
      }
    }

    return resolved;
  }, [selectedIds, storeValues, pendingCreates, contents, categoryName, currentSelectedId]);

  const previewPayload = useMemo<CssContentPreviewRequest[]>(() => {
    if (!previewSelections || previewSelections.length === 0) {
      return [];
    }

    return previewSelections.map((item) => ({
      cssContentId: item.cssContentId || undefined,
      categoryName: item.categoryName,
      originCssName: item.originCssName,
      name: item.name,
      cssName: item.cssName,
      cssValue: item.cssValue,
      uploadFileId: typeof item.uploadFileId === 'string'
        ? item.uploadFileId
        : (item.uploadFileId ? (item.uploadFileId as any).id : undefined)
    }));
  }, [previewSelections]);

  const thumbnailId = productDetail?.thumbnailFile?.id;
  const inlineId = productDetail?.inlineFile?.id;
  const endId = productDetail?.endFile?.id;

  useEffect(() => {
    if (!productId || !thumbnailId || !inlineId) {
      return;
    }

    const requestKey = JSON.stringify({
      productId,
      thumbnailId,
      inlineId,
      endId: endId || null,
      cssContents: previewPayload,
    });

    if (previewRequestTimeoutRef.current) {
      clearTimeout(previewRequestTimeoutRef.current);
      previewRequestTimeoutRef.current = null;
    }

    if (lastPreviewRequestKeyRef.current === requestKey) {
      const cached = previewCacheRef.current.get(requestKey);
      if (cached) {
        setPreviewHtml(cached);
      }
      return;
    }

    const cached = previewCacheRef.current.get(requestKey);
    if (cached) {
      setPreviewHtml(cached);
      lastPreviewRequestKeyRef.current = requestKey;
      return;
    }

    let cancelled = false;

    const fetchPreview = async () => {
      if (previewRequestInFlightRef.current === requestKey) {
        return;
      }

      previewRequestInFlightRef.current = requestKey;

      try {
        const html = await getProductPreview(
          productId,
          thumbnailId,
          inlineId,
          endId,
          {
            cssContents: previewPayload,
          }
        );

        if (!cancelled) {
          setPreviewHtml(html);
          previewCacheRef.current.set(requestKey, html);
          lastPreviewRequestKeyRef.current = requestKey;
        }
      } catch {
        if (!cancelled) {
          previewCacheRef.current.delete(requestKey);
          lastPreviewRequestKeyRef.current = '';
        }
      } finally {
        if (previewRequestInFlightRef.current === requestKey) {
          previewRequestInFlightRef.current = null;
        }
      }
    };

    previewRequestTimeoutRef.current = setTimeout(() => {
      previewRequestTimeoutRef.current = null;
      fetchPreview();
    }, 200);

    return () => {
      cancelled = true;
      if (previewRequestTimeoutRef.current) {
        clearTimeout(previewRequestTimeoutRef.current);
        previewRequestTimeoutRef.current = null;
      }
    };
  }, [productId, thumbnailId, inlineId, endId, previewPayload, getProductPreview]);

  useEffect(() => {
    lastPreviewRequestKeyRef.current = '';
    previewRequestInFlightRef.current = null;
    previewCacheRef.current.clear();
    if (previewRequestTimeoutRef.current) {
      clearTimeout(previewRequestTimeoutRef.current);
      previewRequestTimeoutRef.current = null;
    }
  }, [productId]);



  const attachIframeRef = useCallback((iframe: HTMLIFrameElement | null) => {
    iframeRef.current = iframe;
    if (iframe) {
        const runLoad = () => {
            const iframeDocument = iframe.contentDocument;
            if (!iframeDocument) {
                return;
            }

            let itemToScrollTo = contents.find(item => (item.cssContentId || item.cssName) === currentSelectedId);
            if (!itemToScrollTo) {
                itemToScrollTo = contents.find(item => (item as any).isUsed);
            }
            if (!itemToScrollTo) {
                itemToScrollTo = contents[0];
            }

            let selector;
            if (itemToScrollTo && itemToScrollTo.cssName) {
                selector = '.' + itemToScrollTo.cssName.replace(/(:|\.)/g, '\\$1');
            } else {
                const originName = pendingOriginForCategory || responseOriginForCategory;
                if (originName) {
                    selector = '.' + originName.replace(/(:|\.)/g, '\\$1');
                }
            }

            if (selector) {
                try {
                    const targetElement = iframeDocument.querySelector(selector);
                    if (targetElement) {
                        targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
                    }
                } catch (e) {
                    console.error(`Error while querying selector "${selector}":`, e);
                }
            }
        };

        iframe.onload = runLoad;
        if (iframe.contentDocument?.readyState === 'complete') {
            runLoad();
        }
    }
  }, [contents, currentSelectedId]);



  // 파일 업로드 트리거
  useEffect(() => {
    const doUpload = async () => {
      if (!uploadFiles || uploadFiles.length === 0) return;
      const file = uploadFiles[0];
      const formData = new FormData();
      formData.append('file', file);
      setIsUploading(true);
      try {
        const res = await apiClient.post('/upload-file/upload', formData);
        const uploaded = res.data;
        setNewItem(prev => ({ ...prev, uploadFileId: uploaded.id }));
        setUploadedPreviewUrl(uploaded.fileFullUrl || '');
      } catch (e) {
        console.error('업로드 실패', e);
      } finally {
        setIsUploading(false);
        setUploadFiles([]);
      }
    };
    void doUpload();
  }, [uploadFiles]);
  useEffect(() => {
    const doUpload = async () => {
      if (!editUploadFiles || editUploadFiles.length === 0) return;
      const file = editUploadFiles[0];
      const formData = new FormData();
      formData.append('file', file);
      setIsEditUploading(true);
      try {
        const res = await apiClient.post('/upload-file/upload', formData);
        const uploaded = res.data;
        setEditItem(prev => ({ ...prev, uploadFileId: uploaded.id, previewUrl: uploaded.fileFullUrl || prev.previewUrl }));
      } catch (e) {
        console.error('업로드 실패', e);
      } finally {
        setIsEditUploading(false);
        setEditUploadFiles([]);
      }
    };
    void doUpload();
  }, [editUploadFiles]);

  if (!categoryName) {
    return (
      <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 min-h-[320px] flex items-center justify-center">
        <div className="text-sm text-gray-500">왼쪽에서 카테고리를 선택하세요.</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-lg p-5 border border-gray-200 h-full overflow-y-auto">
      {/* 헤더 */}
      <div className="text-base font-semibold text-gray-800 mb-4 pb-3 border-b-2 border-gray-200 flex flex-wrap justify-between items-center gap-y-2">
        <div>{categoryName} 항목</div>
        {isAdmin && categoryName && (
          <div className="flex items-center flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-700 whitespace-nowrap">원본 css 명</label>
              <input
                  value={originCssName}
                  onChange={(e) => setOriginCssName(e.target.value)}
                  onBlur={() => {
                    const v = originCssName.trim();
                    if (!categoryName) return;
                    if (v) {
                      addPendingCategory(categoryName, v);
                      setPendingOrigin(categoryName, v);
                    }
                  }}
                  placeholder="실제 변경할 css 명"
                  className="w-48 border border-gray-300 rounded-md px-2 py-1.5 text-xs"
              />
            </div>
            <button
              onClick={() => setShowCreateItem(prev => !prev)}
              className="px-2.5 py-1.5 text-xs border border-gray-300 bg-white text-gray-800 rounded-md hover:bg-gray-50"
            >{showCreateItem ? '항목 추가 닫기' : '항목 추가'}</button>
            <button
              onClick={() => {
                if (!currentSelectedId) return;
                const target = resolveContentById(currentSelectedId);
                if (!target) return;
                if (showEditItem && editingTargetId === (target.cssContentId || target.cssName)) {
                  setShowEditItem(false);
                  resetEditItem();
                  return;
                }
                setShowCreateItem(false);
                setNewItemError('');
                initializeEditItem(target);
                setShowEditItem(true);
              }}
              disabled={!currentSelectedId}
              className={`px-2.5 py-1.5 text-xs border border-gray-300 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                showEditItem ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'
              }`}
            >{showEditItem ? '항목 수정 닫기' : '수정하기'}</button>
          </div>
        )}
      </div>

      {/* 목록 + 미리보기 */}
      <div className="flex flex-col lg:flex-row gap-4 mt-4">
        {/* 왼쪽: 갤러리 목록 */}
        <div className="w-full lg:w-3/5">
          {isAdmin && categoryName && showCreateItem && (
            <div className="mb-3 p-3 border border-gray-200 rounded-lg bg-white">
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center">
                  <input
                    value={newItem.cssName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setNewItem({ ...newItem, cssName: value });
                      if (!value.trim()) {
                        setNewItemError('');
                        return;
                      }
                      setNewItemError(isDuplicateCssName(value) ? '이미 존재하는 항목명입니다.' : '');
                    }}
                    placeholder="신규 css명"
                    className={`border rounded-md px-2 py-1.5 text-xs min-w-0 ${newItemError ? 'border-red-400' : 'border-gray-300'}`}
                  />
                  <div className="flex gap-1.5 items-center justify-end">
                    <label className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-800 rounded-md px-2 py-1 text-[11px] cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                      이미지 선택
                      <input type="file" accept="image/*" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUploadFiles(Array.from(e.target.files || []))} className="absolute w-px h-px p-0 m-[-1px] overflow-hidden clip-rect(0,0,0,0) border-0" disabled={isUploading} />
                    </label>
                    {uploadedPreviewUrl && <img src={uploadedPreviewUrl} alt="업로드 미리보기" className="w-6 h-6 border border-gray-200 rounded" />}
                    {newItem.uploadFileId && <span className="text-[10px] text-green-600">ID: {newItem.uploadFileId}</span>}
                  </div>
                </div>
                <input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} placeholder="사용자 노출명" className="border border-gray-300 rounded-md px-2 py-1.5 text-xs w-full" />
                {newItemError && <div className="text-[11px] text-red-500">{newItemError}</div>}
                <textarea value={newItem.cssValue} onChange={(e) => setNewItem({ ...newItem, cssValue: e.target.value })} placeholder="실제 CSS" rows={4} className="border border-gray-300 rounded-md px-2 py-1.5 text-xs resize-vertical" />
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <button onClick={() => setShowCreateItem(false)} className="template-btn-cancel px-2.5 py-1.5 text-xs">취소</button>
                <button onClick={async () => {
                  if (!productId || !categoryName) return;
                  const cssName = newItem.cssName.trim();
                  if (!cssName) return;
                  if (isDuplicateCssName(cssName)) { setNewItemError('이미 존재하는 항목명입니다.'); return; }
                  if (originCssName.trim()) { addPendingCategory(categoryName, originCssName.trim()); setPendingOrigin(categoryName, originCssName.trim()); }
                  addPendingItem(categoryName, { cssName, cssValue: newItem.cssValue.trim(), name: newItem.name.trim() || undefined, uploadFileId: newItem.uploadFileId || undefined, isUsed: false, previewUrl: uploadedPreviewUrl || undefined });
                  setNewItem({ cssName: '', name: '', uploadFileId: '', cssValue: '' });
                  setUploadedPreviewUrl('');
                  setShowCreateItem(false);
                  setNewItemError('');
                }} className="template-btn-primary px-2.5 py-1.5 text-xs bg-gray-800 text-white rounded-md">임시저장</button>
              </div>
            </div>
          )}
          {isAdmin && categoryName && showEditItem && (
            <div className="mb-3 p-3 border border-blue-500 rounded-lg bg-blue-50">
              <div className="text-xs font-semibold text-blue-600 mb-2">선택한 항목 수정</div>
              <div className="flex flex-col gap-2">
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-center">
                  <input
                    value={editItem.cssName}
                    onChange={(e) => {
                      const value = e.target.value;
                      setEditItem(prev => ({ ...prev, cssName: value }));
                      if (!value.trim()) { setEditItemError(''); return; }
                      const excludeKey = (editItem.cssContentId || editingTargetId || '').trim();
                      setEditItemError(isDuplicateCssName(value, excludeKey) ? '이미 존재하는 항목명입니다.' : '');
                    }}
                    placeholder="css명"
                    className={`border rounded-md px-2 py-1.5 text-xs min-w-0 ${editItemError ? 'border-red-400' : 'border-gray-300'}`}
                  />
                  <div className="flex gap-1.5 items-center justify-end">
                    <label className="inline-flex items-center gap-1.5 border border-gray-300 bg-white text-gray-800 rounded-md px-2 py-1 text-[11px] cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                      이미지 교체
                      <input type="file" accept="image/*" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEditUploadFiles(Array.from(e.target.files || []))} className="absolute w-px h-px p-0 m-[-1px] overflow-hidden clip-rect(0,0,0,0) border-0" disabled={isEditUploading} />
                    </label>
                    {editItem.previewUrl && <img src={editItem.previewUrl} alt="수정 미리보기" className="w-6 h-6 border border-gray-200 rounded" />}
                    {editItem.uploadFileId && <span className="text-[10px] text-green-600">ID: {editItem.uploadFileId}</span>}
                  </div>
                </div>
                <input value={editItem.name} onChange={(e) => setEditItem(prev => ({ ...prev, name: e.target.value }))} placeholder="사용자 노출명" className="border border-gray-300 rounded-md px-2 py-1.5 text-xs w-full" />
                {editItemError && <div className="text-[11px] text-red-500">{editItemError}</div>}
                <textarea value={editItem.cssValue} onChange={(e) => setEditItem(prev => ({ ...prev, cssValue: e.target.value }))} placeholder="실제 CSS" rows={4} className="border border-gray-300 rounded-md px-2 py-1.5 text-xs resize-vertical" />
              </div>
              <div className="mt-2 flex justify-end gap-2">
                <button onClick={() => { setShowEditItem(false); resetEditItem(); }} className="template-btn-cancel px-2.5 py-1.5 text-xs">취소</button>
                <button onClick={() => {
                  if (!categoryName) return;
                  const cssName = editItem.cssName.trim();
                  if (!cssName) return;
                  const excludeKey = (editItem.cssContentId || editingTargetId || '').trim();
                  if (isDuplicateCssName(cssName, excludeKey)) { setEditItemError('이미 존재하는 항목명입니다.'); return; }
                  if (originCssName.trim()) { addPendingCategory(categoryName, originCssName.trim()); setPendingOrigin(categoryName, originCssName.trim()); }
                  addPendingItem(categoryName, { cssContentId: (editItem.cssContentId || '').trim() || undefined, cssName, name: editItem.name.trim() || undefined, cssValue: editItem.cssValue.trim(), uploadFileId: editItem.uploadFileId || undefined, isUsed: editItem.isUsed, previewUrl: editItem.previewUrl || undefined }, editingTargetId ? { previousKey: editingTargetId } : undefined);
                  setShowEditItem(false);
                  resetEditItem();
                }} className="template-btn-primary px-2.5 py-1.5 text-xs bg-gray-800 text-white rounded-md">임시저장</button>
              </div>
            </div>
          )}
          {contents.length > 0 ? (
            <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(90px,1fr))] sm:grid-cols-[repeat(auto-fill,minmax(120px,1fr))]">
              {contents.map((item) => {
                const id = item.cssContentId || item.cssName;
                const selected = currentSelectedId === id;
                const used = (item as any).isUsed ?? (item as any).used ?? false;
                const showUsed = currentSelectedId ? selected : used;
                const active = selected || showUsed;
                const pendingMatch = pendingEntryForCategory?.contents?.find((pending: any) => (item.cssContentId && pending.cssContentId === item.cssContentId) || (!item.cssContentId && pending.cssName === item.cssName));
                const baseCssName = (pendingMatch?.cssName ?? item.cssName) || '';
                const baseCssValue = pendingMatch?.cssValue ?? item.cssValue;
                const userFacingName = (pendingMatch?.name ?? (item as any)?.name ?? '').trim();
                const displayTitle = isAdmin ? baseCssName : (userFacingName || baseCssName);
                const secondaryLabel = isAdmin && userFacingName && userFacingName !== baseCssName ? userFacingName : null;
                const imgUrl = pendingMatch?.previewUrl || item.uploadFileId?.fileFullUrl;
                return (
                  <div
                    key={id}
                    onClick={() => onSelect && onSelect(id)}
                    className={`bg-white rounded-lg overflow-hidden relative cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md ${
                      active ? 'border-2 border-blue-500' : 'border border-gray-200'
                    }`}
                  >
                    {selected && (
                      <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs shadow-md" title="선택됨">
                        ✓
                      </div>
                    )}
                    {pendingMatch && (
                      <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-blue-500 text-white text-[10px] font-semibold shadow-md">
                        수정됨
                      </div>
                    )}
                    <div className="w-full aspect-square bg-gray-50 flex items-center justify-center">
                      {imgUrl ? (
                        <img src={imgUrl} alt={displayTitle} className="w-full h-full object-cover" />
                      ) : (
                        <div className="text-xs text-gray-400">이미지 없음</div>
                      )}
                    </div>
                    <div className="p-2">
                      <div className={`text-xs font-semibold whitespace-nowrap overflow-hidden text-ellipsis ${active ? 'text-blue-800' : 'text-gray-800'}`}>
                        {displayTitle}
                      </div>
                      {isAdmin && secondaryLabel && (
                        <div className="text-[11px] text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis mt-0.5">
                          {secondaryLabel}
                        </div>
                      )}
                      {isAdmin && baseCssValue && (
                        <div className={`text-[11px] text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis ${secondaryLabel ? 'mt-0.5' : 'mt-1'}`}>
                          {baseCssValue}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="p-5 text-center text-xs text-gray-400 bg-white rounded-md border border-gray-200">
              항목이 없습니다
            </div>
          )}
        </div>

        {/* 오른쪽: 미리보기 (iframe) */}
        <div className="w-full lg:w-4/5">
          {/*<h3 className="text-sm font-semibold text-gray-700 mb-2.5">미리보기</h3>*/}
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-white overflow-hidden w-[500px] h-[630px] mx-auto lg:mx-0">
            <iframe
              ref={attachIframeRef}

              className="w-[245mm] h-[330mm] scale-[0.542] origin-top-left border-none block"
              srcDoc={previewHtml}
              title="스타일 미리보기"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StyleListPanel;
